
import { supabase } from '@/integrations/supabase/client';
import { AppRole } from '@/types/auth';
import { securityLogger } from './securityLogger';

export const roleValidationService = {
  async validateUserRole(requiredRole: AppRole): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        await securityLogger.logSecurityEvent('role_validation_failed', {
          reason: 'no_user',
          required_role: requiredRole
        });
        return false;
      }

      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        await securityLogger.logSecurityEvent('role_validation_error', {
          user_id: user.id,
          error: error.message,
          required_role: requiredRole
        });
        return false;
      }

      const hasRole = roles?.some(r => r.role === requiredRole) || false;
      
      if (!hasRole) {
        await securityLogger.logSecurityEvent('insufficient_role_access', {
          user_id: user.id,
          required_role: requiredRole,
          user_roles: roles?.map(r => r.role) || []
        });
      }

      return hasRole;
    } catch (error) {
      await securityLogger.logSecurityEvent('role_validation_exception', {
        error: error instanceof Error ? error.message : 'Unknown error',
        required_role: requiredRole
      });
      return false;
    }
  },

  async validateUserHasAnyRole(requiredRoles: AppRole[]): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        await securityLogger.logSecurityEvent('multi_role_validation_failed', {
          reason: 'no_user',
          required_roles: requiredRoles
        });
        return false;
      }

      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        await securityLogger.logSecurityEvent('multi_role_validation_error', {
          user_id: user.id,
          error: error.message,
          required_roles: requiredRoles
        });
        return false;
      }

      const hasAnyRole = roles?.some(r => requiredRoles.includes(r.role as AppRole)) || false;
      
      if (!hasAnyRole) {
        await securityLogger.logSecurityEvent('insufficient_multi_role_access', {
          user_id: user.id,
          required_roles: requiredRoles,
          user_roles: roles?.map(r => r.role) || []
        });
      }

      return hasAnyRole;
    } catch (error) {
      await securityLogger.logSecurityEvent('multi_role_validation_exception', {
        error: error instanceof Error ? error.message : 'Unknown error',
        required_roles: requiredRoles
      });
      return false;
    }
  },

  async getUserRolePriority(): Promise<number> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { data, error } = await supabase.rpc('get_user_role_priority', {
        _user_id: user.id
      });

      if (error) {
        await securityLogger.logSecurityEvent('role_priority_error', {
          user_id: user.id,
          error: error.message
        });
        return 0;
      }

      return data || 0;
    } catch (error) {
      return 0;
    }
  }
};
